import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "searchInput", "searchButton", "loading", "searchError",
    "invoiceDetails", "invoiceNumber", "invoiceType", "invoiceDate",
    "invoiceTotal", "invoiceCae", "invoiceDescription",
    "colppyInvoiceId", "orderSelect", "orderAmounts", "orderAmountsList",
    "netAmount", "colppyTotal", "amountMismatch", "submitButton"
  ]

  static values = {
    patientId: Number,
    searchUrl: String
  }

  connect() {
    this.colppyTotalCents = 0
    this.updateSubmitState()
  }

  handleKeypress(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.searchInvoice()
    }
  }

  async searchInvoice() {
    const nroFactura = this.searchInputTarget.value.trim()
    if (!nroFactura) return

    this.loadingTarget.style.display = "block"
    this.searchErrorTarget.style.display = "none"
    this.invoiceDetailsTarget.style.display = "none"

    try {
      const url = `${this.searchUrlValue}?nro_factura=${encodeURIComponent(nroFactura)}&document_type=FAV`
      const response = await fetch(url, {
        headers: { "Accept": "application/json", "X-CSRF-Token": this.csrfToken }
      })
      const data = await response.json()

      this.loadingTarget.style.display = "none"

      if (data.success && data.invoices && data.invoices.length > 0) {
        const invoice = data.invoices[0]

        if (invoice.already_registered) {
          this.searchErrorTarget.textContent = "Esta factura ya está registrada en el sistema"
          this.searchErrorTarget.style.display = "block"
          return
        }

        this.displayInvoice(invoice)
      } else {
        this.searchErrorTarget.textContent = data.error || "No se encontró la factura en Colppy"
        this.searchErrorTarget.style.display = "block"
      }
    } catch (error) {
      this.loadingTarget.style.display = "none"
      this.searchErrorTarget.textContent = `Error de conexión: ${error.message}`
      this.searchErrorTarget.style.display = "block"
    }
  }

  displayInvoice(invoice) {
    this.invoiceNumberTarget.textContent = invoice.nro_factura_full || invoice.nro_factura
    this.invoiceTypeTarget.textContent = `FC ${invoice.invoice_type}`
    this.invoiceDateTarget.textContent = invoice.date_of_issue || "-"
    const totalPesos = (invoice.total_factura_cents / 100).toFixed(2)
    this.invoiceTotalTarget.textContent = `$${totalPesos}`
    this.invoiceCaeTarget.textContent = invoice.cae || "-"
    this.invoiceDescriptionTarget.textContent = invoice.description || "-"

    this.colppyInvoiceIdTarget.value = invoice.id_factura
    this.colppyTotalCents = invoice.total_factura_cents
    this.colppyTotalTarget.textContent = totalPesos

    this.invoiceDetailsTarget.style.display = "block"
    // Si hay órdenes pre-seleccionadas, calcular montos automáticamente
    const hasPreselected = Array.from(this.orderSelectTarget.options).some(opt => opt.selected && opt.value !== "")
    if (hasPreselected) {
      this.calculateNetAmount()
    } else {
      this.updateSubmitState()
    }
  }

  calculateNetAmount() {
    // Use setTimeout to ensure slim-select has finished updating the native <select>
    setTimeout(() => {
      const orderSelect = this.orderSelectTarget
      const selectedValues = Array.from(orderSelect.options).filter(opt => opt.selected)
      const orderAmountsList = this.orderAmountsListTarget
      const orderAmountsDiv = this.orderAmountsTarget

      if (selectedValues.length > 0) {
        orderAmountsDiv.style.display = "block"
        orderAmountsList.innerHTML = ""

        selectedValues.forEach((option) => {
          const orderId = option.value
          if (!orderId) return

          const orderText = option.text
          const remainingCents = parseInt(option.getAttribute("data-remaining-cents")) || 0
          const remainingPesos = remainingCents / 100

          const amountDiv = document.createElement("div")
          amountDiv.className = "form-group"
          const inputValue = remainingPesos > 0 ? remainingPesos.toFixed(2) : "0.00"
          amountDiv.innerHTML = `
            <label>${orderText}</label>
            <input type="number"
                   name="order_amounts[${orderId}]"
                   class="form-control colppy-import-order-amount"
                   min="0"
                   max="${remainingPesos.toFixed(2)}"
                   step="0.01"
                   value="${inputValue}"
                   data-action="input->colppy-import-invoice#updateTotalAmount blur->colppy-import-invoice#formatInputValue">
          `
          orderAmountsList.appendChild(amountDiv)
        })
      } else {
        orderAmountsDiv.style.display = "none"
        orderAmountsList.innerHTML = ""
      }

      this.updateTotalAmount()
    })
  }

  formatInputValue(event) {
    const input = event.target
    const value = parseFloat(input.value) || 0
    input.value = value.toFixed(2)
  }

  updateTotalAmount() {
    const amountInputs = this.element.querySelectorAll(".colppy-import-order-amount")
    let totalPesos = 0
    amountInputs.forEach((input) => {
      totalPesos += parseFloat(input.value) || 0
    })

    this.netAmountTarget.textContent = totalPesos.toFixed(2)

    // Check mismatch: solo alertar si las órdenes superan el total de la factura
    const totalCents = Math.round(totalPesos * 100)
    if (this.colppyTotalCents > 0 && totalCents > this.colppyTotalCents) {
      this.amountMismatchTarget.style.display = "block"
    } else {
      this.amountMismatchTarget.style.display = "none"
    }

    this.updateSubmitState()
  }

  updateSubmitState() {
    if (!this.hasSubmitButtonTarget) return

    const hasInvoice = this.hasColppyInvoiceIdTarget && this.colppyInvoiceIdTarget.value !== ""
    const hasOrders = this.hasOrderSelectTarget && this.orderSelectTarget.selectedOptions &&
      Array.from(this.orderSelectTarget.selectedOptions).some(opt => opt.value !== "")

    // Check amounts: órdenes no pueden superar el total de la factura (pero pueden ser menores)
    const amountInputs = this.element.querySelectorAll(".colppy-import-order-amount")
    let totalCents = 0
    amountInputs.forEach((input) => {
      totalCents += Math.round((parseFloat(input.value) || 0) * 100)
    })
    const amountsValid = this.colppyTotalCents > 0 && totalCents > 0 && totalCents <= this.colppyTotalCents

    this.submitButtonTarget.disabled = !(hasInvoice && hasOrders && amountsValid)
  }

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.getAttribute("content") : ""
  }
}
